import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Wrench, AlertTriangle, ArrowRight, Clock, CheckCircle, Bell } from "lucide-react";
import { Link } from "wouter";
import { Skeleton } from "@/components/ui/skeleton";
import { ActiveWatchesDrawer, type ActiveWatch } from "./active-watches-drawer";

export default function RepairFollowupWidget() {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data: pendingReminders, isLoading: loadingPending } = useQuery<ActiveWatch[]>({
    queryKey: ["/api/repair-followups/pending-reminders"],
  });

  const { data: allActiveWatches, isLoading: loadingActive } = useQuery<ActiveWatch[]>({
    queryKey: ["/api/repair-followups"],
  });

  const { data: completedCountData, isLoading: loadingCompleted } = useQuery<{ count: number }>({
    queryKey: ["/api/repair-followups/completed-count"],
  });

  const isLoading = loadingPending || loadingActive || loadingCompleted;
  const pendingCount = pendingReminders?.length || 0;
  const activeCount = allActiveWatches?.length || 0;
  
  const now = new Date();
  const overdueCount = pendingReminders?.filter(w => {
    const dueAt = new Date(w.reminderDueAt);
    return dueAt < now;
  }).length || 0;

  const completedCount = completedCountData?.count || 0;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            Repair Follow-ups
          </CardTitle>
          <CardDescription>Claims in repair status</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <Card data-testid="repair-followup-widget">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Wrench className="h-5 w-5" />
          Repair Follow-ups
        </CardTitle>
        <CardDescription>Claims awaiting repair progress updates</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div
            className="text-center p-3 bg-blue-50 dark:bg-blue-950 rounded-lg cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors"
            onClick={() => setDrawerOpen(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && setDrawerOpen(true)}
            data-testid="widget-active-watches-clickable"
          >
            <p className="text-2xl font-bold text-blue-700 dark:text-blue-300" data-testid="widget-active-watches">
              {activeCount}
            </p>
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              <Clock className="h-3 w-3" />
              Active Watches
            </p>
          </div>
          <div className="text-center p-3 bg-amber-50 dark:bg-amber-950 rounded-lg">
            <p className="text-2xl font-bold text-amber-700 dark:text-amber-300" data-testid="widget-pending-reminders">
              {pendingCount}
            </p>
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              <Bell className="h-3 w-3" />
              Pending
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          {overdueCount > 0 && (
            <Badge variant="destructive" className="flex items-center gap-1" data-testid="widget-overdue-count">
              <AlertTriangle className="h-3 w-3" />
              {overdueCount} Overdue
            </Badge>
          )}
          {completedCount > 0 && (
            <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 flex items-center gap-1" data-testid="widget-completed-count">
              <CheckCircle className="h-3 w-3" />
              {completedCount} Ready for Depreciation
            </Badge>
          )}
        </div>

        {activeCount > 0 ? (
          <div className="space-y-2">
            {pendingCount > 0 && (
              <p className="text-sm text-muted-foreground">
                {pendingCount} claim{pendingCount !== 1 ? 's' : ''} need{pendingCount === 1 ? 's' : ''} follow-up
              </p>
            )}
            <Link href="/claims?status=contractor_in_production_admin">
              <Button variant="outline" className="w-full" data-testid="widget-view-repair-claims">
                View Repair Claims
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground">
              No active repair follow-ups
            </p>
          </div>
        )}
      </CardContent>
    </Card>
    <ActiveWatchesDrawer
      open={drawerOpen}
      onOpenChange={setDrawerOpen}
      watches={allActiveWatches || []}
    />
    </>
  );
}
